import { useState, useCallback, useRef } from 'react';
import { editToSegments, applyStyleToRange, applyStyleToAll, isBoxLevelStyle, DEFAULT_SEGMENT_STYLES } from '@/lib/richText';

let editIdCounter = 0;
const MAX_HISTORY = 50;

/**
 * useEdits — Manages text and image overlays on PDF pages.
 *
 * Edits are stored in page-coordinate space (origin top-left, scale=1).
 * The EditLayer converts to screen space using zoom.
 *
 * Supports undo/redo history and multi-select (Shift+click).
 * Drag/resize intermediate states bypass history — call commitUpdate()
 * on mouseup to snapshot the final position.
 */
export default function useEdits() {
  const [edits, setEdits] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editTool, setEditTool] = useState('select'); // 'select' | 'text' | 'image' | 'date' | 'signature' | 'editText'
  const [selectedEditIds, setSelectedEditIds] = useState(new Set());
  const [editStyles, setEditStyles] = useState({
    fontFamily: 'Helvetica',
    fontSize: 14,
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    color: '#000000',
    alignment: 'left',
    backgroundColor: 'transparent',
    lineHeight: 1.3,
  });

  // --- Undo/redo history ---
  const historyRef = useRef([[]]);
  const historyIdxRef = useRef(0);
  const editsRef = useRef(edits);
  editsRef.current = edits;
  const selectedRef = useRef(selectedEditIds);
  selectedRef.current = selectedEditIds;
  const [clipboard, setClipboard] = useState([]);

  const pushHistory = useCallback((newEdits) => {
    const h = historyRef.current;
    const idx = historyIdxRef.current;
    historyRef.current = [...h.slice(0, idx + 1), newEdits].slice(-MAX_HISTORY);
    historyIdxRef.current = historyRef.current.length - 1;
    // Sync the ref immediately (not just on the next render) so callers that
    // chain two ref-reading writers synchronously in the same handler (e.g.
    // `const id = addEdit(edit); deleteEdit(id);` — Viewer's candidate
    // promotion) see their own just-pushed edit instead of a stale array.
    // React batches the setEdits below, so without this the second call
    // would silently operate on pre-first-call state and lose the edit.
    editsRef.current = newEdits;
    setEdits(newEdits);
  }, []);

  const undo = useCallback(() => {
    // Typing/dragging updates the live draft between history commits. Preserve
    // it before stepping back so Redo cannot resurrect an old placeholder.
    if (historyRef.current[historyIdxRef.current] !== editsRef.current) {
      historyRef.current = [...historyRef.current.slice(0, historyIdxRef.current + 1), editsRef.current].slice(-MAX_HISTORY);
      historyIdxRef.current = historyRef.current.length - 1;
    }
    const idx = historyIdxRef.current;
    if (idx > 0) {
      historyIdxRef.current = idx - 1;
      const prevEdits = historyRef.current[idx - 1];
      // Sync the ref immediately (see pushHistory's comment) so a synchronous
      // caller chaining a second ref-reading writer right after undo() in the
      // same handler (no intervening render) sees post-undo state.
      editsRef.current = prevEdits;
      setEdits(prevEdits);
    }
  }, []);

  const redo = useCallback(() => {
    const idx = historyIdxRef.current;
    const h = historyRef.current;
    if (h && idx < h.length - 1) {
      historyIdxRef.current = idx + 1;
      const nextEdits = h[idx + 1];
      // Sync the ref immediately — see undo()'s comment.
      editsRef.current = nextEdits;
      setEdits(nextEdits);
    }
  }, []);

  // --- CRUD ---

  const addEdit = useCallback((edit) => {
    const id = `edit-${++editIdCounter}`;
    const newEdit = { id, ...edit };
    if (newEdit.type === 'text' && newEdit.source !== 'detected') newEdit.textLayoutVersion = 2;
    // For text edits, initialize segments from content + styles
    if (newEdit.type === 'text' && !newEdit.segments) {
      const segStyles = {};
      for (const key of ['fontFamily', 'fontSize', 'bold', 'italic', 'underline', 'strikethrough', 'color']) {
        segStyles[key] = newEdit.styles?.[key] ?? DEFAULT_SEGMENT_STYLES[key];
      }
      newEdit.segments = [{ text: newEdit.content || '', styles: segStyles }];
    }
    const next = [...editsRef.current, newEdit];
    pushHistory(next);
    setSelectedEditIds(new Set([id]));
    return id;
  }, [pushHistory]);

  /** Batch-add multiple edits as a single history entry (used by content detection). */
  const addEdits = useCallback((editArray) => {
    const newEdits = editArray.map((edit) => {
      const id = `edit-${++editIdCounter}`;
      const newEdit = { id, ...edit };
    if (newEdit.type === 'text' && newEdit.source !== 'detected') newEdit.textLayoutVersion = 2;
      if (newEdit.type === 'text' && !newEdit.segments) {
        const segStyles = {};
        for (const key of ['fontFamily', 'fontSize', 'bold', 'italic', 'underline', 'strikethrough', 'color']) {
          segStyles[key] = newEdit.styles?.[key] ?? DEFAULT_SEGMENT_STYLES[key];
        }
        newEdit.segments = [{ text: newEdit.content || '', styles: segStyles }];
      }
      return newEdit;
    });
    const next = [...editsRef.current, ...newEdits];
    pushHistory(next);
    setSelectedEditIds(new Set());
  }, [pushHistory]);

  // An edit is "dirty" (needs redraw on save) if it's detected content we've
  // touched, or a restored overlay whose prior flat-draw no longer matches
  // what we want in the PDF. Both need to set `modified` on first mutation so
  // saveAllChanges can tell them apart from unchanged restored overlays that
  // should be left alone.
  const needsModifiedFlag = (e) => (
    (e.source === 'detected' || e.restored) && !e.modified
  );

  /** Immediate update without history push — use for drag/resize intermediate states. */
  const updateEdit = useCallback((id, updates) => {
    setEdits((prev) => prev.map((e) => {
      if (e.id !== id) return e;
      if (e.rotationLocked) return e;
      const updated = { ...e, ...updates };
      if (e.type === 'text' && (updates.segments || updates.content !== undefined || updates.styles)) updated.textLayoutVersion = 2;
      if (needsModifiedFlag(e)) updated.modified = true;
      return updated;
    }));
  }, []);

  /** Batch-update multiple edits without history push. updates: Map<id, partial>. */
  const updateEdits = useCallback((updates) => {
    setEdits((prev) => prev.map((e) => {
      if (!updates.has(e.id)) return e;
      if (e.rotationLocked) return e;
      const updated = { ...e, ...updates.get(e.id) };
      if (needsModifiedFlag(e)) updated.modified = true;
      return updated;
    }));
  }, []);

  /** Snapshot current edits into history. Call on mouseup after drag/resize. */
  const commitUpdate = useCallback(() => {
    const current = editsRef.current;
    const h = historyRef.current;
    const idx = historyIdxRef.current;
    if (h[idx] !== current) {
      historyRef.current = [...h.slice(0, idx + 1), current].slice(-MAX_HISTORY);
      historyIdxRef.current = historyRef.current.length - 1;
    }
  }, []);

  // An edit has a "baked drawing" when some previous save flattened it into
  // the PDF — either a detected content block with originalBounds, or an
  // overlay restored from the Scrivar sidecar with bakedBounds. Deleting
  // these can't just drop the edit; we need to keep it as a tombstone so the
  // next save paints a whiteout over the flattened pixels. Otherwise the
  // old version ghosts through.
  const hasBakedDrawing = (e) => !!(
    e && (
      (e.source === 'detected' && e.originalBounds) ||
      e.restored ||
      e.bakedBounds
    )
  );

  const deleteEdit = useCallback((id) => {
    const edit = editsRef.current.find((e) => e.id === id);
    if (edit?.rotationLocked) return;
    let next;
    if (hasBakedDrawing(edit)) {
      next = editsRef.current.map((e) =>
        e.id === id ? { ...e, deleted: true, content: '' } : e
      );
    } else {
      next = editsRef.current.filter((e) => e.id !== id);
    }
    pushHistory(next);
    setSelectedEditIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  }, [pushHistory]);

  const deleteSelected = useCallback(() => {
    const sel = selectedRef.current;
    if (!sel || sel.size === 0) return;
    const next = editsRef.current.reduce((acc, e) => {
      if (!sel.has(e.id) || e.rotationLocked) {
        acc.push(e);
      } else if (hasBakedDrawing(e)) {
        acc.push({ ...e, deleted: true, content: '' });
      }
      // else: unsaved edit — remove entirely, nothing flattened to clean up
      return acc;
    }, []);
    pushHistory(next);
    setSelectedEditIds(new Set());
  }, [pushHistory]);

  /** Reset all edits and history — call after save to prevent duplicates. */
  const clearEdits = useCallback(() => {
    historyRef.current = [[]];
    historyIdxRef.current = 0;
    // Sync the ref immediately — see undo()'s comment.
    editsRef.current = [];
    setEdits([]);
    setSelectedEditIds(new Set());
  }, []);

  // --- Clipboard ---

  /** Copy selected items to internal clipboard (strips id and pageNum). */
  const copySelected = useCallback(() => {
    const sel = selectedRef.current;
    if (!sel || sel.size === 0) return;
    const copied = editsRef.current
      .filter((e) => sel.has(e.id))
      .map(({ id, pageNum, editing, ...rest }) => ({ ...rest, sourcePageNum: pageNum, sourceX: rest.x, sourceY: rest.y }));
    setClipboard(copied);
  }, []);

  /** Paste clipboard items onto targetPage. Offsets +20px if same page as source.
   *  Locked sources (rotationLocked) are excluded — a locked edit is read-only,
   *  so pasting it would produce an unlocked copy of content we deliberately
   *  don't let anyone touch. */
  const pasteEdits = useCallback((targetPage, pageWidth, pageHeight) => {
    const pasteable = clipboard.filter((c) => !c.rotationLocked);
    if (pasteable.length === 0) return;
    const samePage = pasteable.every((c) => c.sourcePageNum === targetPage);
    const offset = samePage ? 20 : 0;

    const newIds = [];
    const current = editsRef.current;
    const newEdits = pasteable.map((item) => {
      const id = `edit-${++editIdCounter}`;
      newIds.push(id);
      const { sourcePageNum, sourceX, sourceY, ...rest } = item;
      return {
        ...rest,
        id,
        pageNum: targetPage,
        x: Math.min(rest.x + offset, pageWidth - rest.width),
        y: Math.min(rest.y + offset, pageHeight - rest.height),
      };
    });

    pushHistory([...current, ...newEdits]);
    setSelectedEditIds(new Set(newIds));
  }, [clipboard, pushHistory]);

  /** Duplicate = copy + immediate paste on the same page. Locked selections
   *  are excluded — see pasteEdits. */
  const duplicateSelected = useCallback((pageWidth, pageHeight) => {
    const sel = selectedRef.current;
    if (!sel || sel.size === 0) return;
    const items = editsRef.current.filter((e) => sel.has(e.id) && !e.rotationLocked);
    if (items.length === 0) return;

    const newIds = [];
    const newEdits = items.map((item) => {
      const id = `edit-${++editIdCounter}`;
      newIds.push(id);
      const { id: _id, editing, ...rest } = item;
      return {
        ...rest,
        id,
        x: Math.min(rest.x + 20, pageWidth - rest.width),
        y: Math.min(rest.y + 20, pageHeight - rest.height),
      };
    });

    pushHistory([...editsRef.current, ...newEdits]);
    setSelectedEditIds(new Set(newIds));
  }, [pushHistory]);

  // --- Selection ---

  const selectEdit = useCallback((id) => {
    setSelectedEditIds(new Set([id]));
  }, []);

  /** Shift+click: toggle an item in/out of the selection set. */
  const toggleSelect = useCallback((id) => {
    setSelectedEditIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedEditIds(new Set());
  }, []);

  // --- Styles ---

  const updateStyle = useCallback((key, value) => {
    setEditStyles((prev) => ({ ...prev, [key]: value }));
    const current = editsRef.current;
    const sel = selectedRef.current;
    if (!sel || sel.size === 0) return;
    const hasChange = current.some((e) => sel.has(e.id) && e.type === 'text' && !e.rotationLocked);
    if (!hasChange) return;

    const dirtyPatch = (e) => ({ textLayoutVersion: 2, ...(needsModifiedFlag(e) ? { modified: true } : {}) });

    if (isBoxLevelStyle(key)) {
      // Box-level styles stay on the edit's styles object
      const next = current.map((e) =>
        sel.has(e.id) && e.type === 'text' && !e.rotationLocked
          ? { ...e, styles: { ...e.styles, [key]: value }, ...dirtyPatch(e) }
          : e
      );
      pushHistory(next);
    } else {
      // Segment-level styles: apply to ALL segments (no selection context here)
      const next = current.map((e) => {
        if (!sel.has(e.id) || e.type !== 'text' || e.rotationLocked) return e;
        const segments = editToSegments(e);
        return { ...e, segments: applyStyleToAll(segments, key, value), ...dirtyPatch(e) };
      });
      pushHistory(next);
    }
  }, [pushHistory]);

  /** Apply a style change to a character range within a text edit's segments.
   *  If start === end (cursor, no selection), applies to the whole box. */
  const updateSegmentStyle = useCallback((editId, key, value, start, end) => {
    const current = editsRef.current;
    const edit = current.find((e) => e.id === editId);
    if (!edit || edit.type !== 'text' || edit.rotationLocked) return;

    const segments = editToSegments(edit);
    const hasSelection = start !== undefined && end !== undefined && start < end;
    const newSegments = hasSelection
      ? applyStyleToRange(segments, start, end, key, value)
      : applyStyleToAll(segments, key, value);

    const next = current.map((e) =>
      e.id === editId ? { ...e, textLayoutVersion: 2, segments: newSegments, ...(needsModifiedFlag(e) ? { modified: true } : {}) } : e
    );
    pushHistory(next);
  }, [pushHistory]);

  /** Replace all edits with a restored set. Resets undo history. */
  const restoreEdits = useCallback((editArray) => {
    const restored = editArray.map((e) => {
      // Preserve original ID from sidecar so hasUnsavedChanges tracking matches.
      // Only assign a new ID if the incoming edit has none (defensive).
      const id = e.id || `edit-${++editIdCounter}`;
      // Also bump the counter past any numeric suffix we see, to avoid collision
      // with future addEdit calls.
      const match = typeof e.id === 'string' ? e.id.match(/^edit-(\d+)$/) : null;
      if (match) {
        const n = parseInt(match[1], 10);
        if (n > editIdCounter) editIdCounter = n;
      }
      const edit = { ...e, id, restored: true };
      // Migrate legacy text edits (content string, no segments) to segments
      if (edit.type === 'text' && !edit.segments && edit.content) {
        const segStyles = {};
        for (const key of ['fontFamily', 'fontSize', 'bold', 'italic', 'underline', 'strikethrough', 'color']) {
          segStyles[key] = edit.styles?.[key] ?? DEFAULT_SEGMENT_STYLES[key];
        }
        edit.segments = [{ text: edit.content, styles: segStyles }];
      }
      // Track the position at which this edit was baked into the PDF, so that
      // if the user moves it and re-saves we can white-out the original bounds.
      //
      // ONLY for edits whose pixels are actually IN the content stream. An
      // apCarried edit (tagged by usePDF when its /AP annot was stripped from
      // the opened bytes) has no baked pixels at all — fabricating bakedBounds
      // for it makes MaskLayer and the save whiteout paint white over raw page
      // pixels, a glaring white box behind signatures on scanned documents.
      // Untagged entries keep legacy semantics (pre-/AP saves baked everything).
      if (!e.apCarried) {
        edit.bakedBounds = {
          x: edit.x,
          y: edit.y,
          width: edit.width,
          height: edit.height,
        };
      }
      return edit;
    });
    historyRef.current = [restored];
    historyIdxRef.current = 0;
    // Sync the ref immediately — see undo()'s comment.
    editsRef.current = restored;
    setEdits(restored);
    setSelectedEditIds(new Set());
  }, []);

  const canUndo = historyIdxRef.current > 0;
  const canRedo = historyRef.current ? historyIdxRef.current < historyRef.current.length - 1 : false;

  return {
    edits,
    editMode,
    editTool,
    selectedEditIds,
    editStyles,
    canUndo,
    canRedo,
    setEditMode,
    setEditTool,
    addEdit,
    addEdits,
    updateEdit,
    updateEdits,
    commitUpdate,
    deleteEdit,
    deleteSelected,
    selectEdit,
    toggleSelect,
    deselectAll,
    updateStyle,
    updateSegmentStyle,
    undo,
    redo,
    clearEdits,
    clipboard,
    copySelected,
    pasteEdits,
    duplicateSelected,
    setSelectedEditIds,
    restoreEdits,
  };
}
